import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export type CategoriaGlosa = 'quantitativa' | 'financeira';
export type EscopoGlosa = 'item' | 'contrato';
export type AbrangenciaGlosa = 'total' | 'parcial';

// Legacy compat
export type TipoGlosa = 'total' | 'parcial' | 'financeira';
export type TipoGlosaCat = 'quantitativa' | 'financeira';

export const NATUREZA_OPTIONS = [
  { value: 'erro_medicao', label: 'Erro de medição' },
  { value: 'penalidade_contratual', label: 'Penalidade contratual' },
  { value: 'ajuste_comercial', label: 'Ajuste comercial' },
  { value: 'nao_conformidade', label: 'Não conformidade' },
  { value: 'documentacao', label: 'Documentação' },
  { value: 'outro', label: 'Outros' },
] as const;

export interface GlosarItemInput {
  // New unified model
  categoria: CategoriaGlosa;
  escopo: EscopoGlosa;
  abrangencia?: AbrangenciaGlosa;
  // Context
  medicaoItemId?: string;
  contratoId: string;
  boletimId?: string;
  // Values
  quantidadeAprovada?: number;
  valorGlosado?: number;
  natureza?: string;
  justificativa: string;
}

export interface GlosarItemResult {
  sucesso: boolean;
  categoria: CategoriaGlosa;
  escopo: EscopoGlosa;
  tipo_glosa: string;
  tipo_glosa_categoria: TipoGlosaCat;
  medicao_item_id: string | null;
  execucao_filha_id: string | null;
  glosa_id: string | null;
  quantidade_aprovada: number;
  quantidade_glosada: number;
  valor_glosado: number;
  valor_delta: number;
  impacto_resumido: string;
  hash_registro: string;
}

export const ITEM_STATUS_LABELS: Record<string, string> = {
  em_analise_cliente: 'Em análise',
  aprovado: 'Aprovado',
  aprovado_parcialmente: 'Aprovado parcialmente',
  glosado: 'Glosado',
  aprovado_com_ajuste: 'Ajuste financeiro',
  reprovado: 'Reprovado',
};

export const ITEM_STATUS_COLORS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  em_analise_cliente: 'secondary',
  aprovado: 'default',
  aprovado_parcialmente: 'outline',
  glosado: 'destructive',
  aprovado_com_ajuste: 'outline',
  reprovado: 'destructive',
};

export function useGlosarItemMedicao() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: GlosarItemInput): Promise<GlosarItemResult> => {
      if (!user) throw new Error('Não autenticado');

      const { data, error } = await supabase.rpc('emp_glosar_item_medicao', {
        p_medicao_item_id: input.medicaoItemId ?? null,
        p_tipo_glosa: input.categoria === 'financeira' ? 'financeira' : (input.abrangencia ?? 'total'),
        p_justificativa: input.justificativa,
        p_quantidade_aprovada: input.quantidadeAprovada ?? null,
        p_user_id: user.id,
        p_valor_glosado: input.valorGlosado ?? null,
        p_natureza: input.natureza ?? null,
        // New unified params
        p_categoria: input.categoria,
        p_escopo: input.escopo,
        p_abrangencia: input.abrangencia ?? null,
        p_contrato_id: input.contratoId,
      } as any);

      if (error) throw error;
      return data as unknown as GlosarItemResult;
    },
    onSuccess: (result, input) => {
      // Invalidate all relevant queries
      const keys = [
        ['emp-medicao-itens', input.boletimId],
        ['emp-boletins', input.contratoId],
        ['emp-glosas', input.boletimId],
        ['emp-glosas-contrato', input.contratoId],
        ['emp-glosas-resumo', input.contratoId],
        ['emp-eventos-negocio', input.contratoId],
        ['emp-saldo-medicao'],
        ['emp-execucoes'],
      ].filter(k => k[1] !== undefined);

      keys.forEach(k => qc.invalidateQueries({ queryKey: k }));

      toast({
        title: '✓ Glosa aplicada',
        description: result.impacto_resumido,
      });
    },
    onError: (e: Error) => {
      toast({
        title: 'Erro ao aplicar glosa',
        description: e.message,
        variant: 'destructive',
      });
    },
  });
}
